import React, { useState, useEffect, useContext, useRef } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { Modal, Offcanvas, Dropdown } from "react-bootstrap";
import { BiSearch, BiCart, BiUser, BiX, BiChevronDown, BiChevronLeft, BiLogOut, BiPackage, BiHeart, BiPlus, BiNews, BiCategory, BiCog, BiUserCircle, BiBrain, BiBookOpen, BiGlobe, BiCompass } from "react-icons/bi";
import { FaBars } from "react-icons/fa";
import useAxios from "services/useAxios.tsx";
import CardItemContext from "../../../context/CardItem/CardItemContext";
import WithAuthentication from "context/Authentication/AuthenticationContext.ts";
import ManagementContext from "context/WebInfo/ManagementContext";
import Container from "../../LogIn/Container";
import Basket from "./Basket";
import SearchDrawer from "../MobileHeader/SearchDrawer";
import { debounce } from "lodash";
import "./header.css";

const STATIC_CATEGORIES = [
  { id: 1, txt: "روانشناسی", icon: <BiBrain /> },
  { id: 2, txt: "عمومی", icon: <BiGlobe /> },
  { id: 3, txt: "رمان", icon: <BiBookOpen /> },
  { id: 4, txt: "فلسفه", icon: <BiCompass /> },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Actions State
  const [loginShow, setLoginShow] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [foundCategories, setFoundCategories] = useState([]);
  const [articles, setArticles] = useState([]);

  const { itemCount, setShowOffcanvas } = useContext(CardItemContext);
  const auth = useContext(WithAuthentication);
  const { setControllerProduct, setShowModalArticle, setType } = useContext(ManagementContext);
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);

  // API Hooks
  // Updated API endpoint for categories
  const { execute: searchApi } = useAxios("api3/search");

  // Search Logic
  const callApiSearch = (query) => {
    const data = { title: query, page: 1, count: 6 };
    if (query !== "") {
      searchApi(data).then((res) => {
        setProducts(res.products?.slice(0, 5) || []);
        setFoundCategories(res.hashtags?.slice(0, 5) || []);
        setArticles(res.post?.slice(0, 5) || []);
      });
    }
  };

  const searchDebounce = useRef(
    debounce((text) => {
      callApiSearch(text);
    }, 800)
  );

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    searchDebounce.current(val);
  };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearch(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavClick = (hash) => {
    setMobileMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) element.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      const element = document.getElementById(hash);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header className={`custom-header ${scrolled ? "scrolled" : ""}`}>
        <div className="header-container">
          {/* Brand */}
          <NavLink to="/" className="header-brand" onClick={() => setMobileMenuOpen(false)}>
            <div className="brand-text">
              <span className="brand-title">دکتر ابوالفضل خان‌بیکی</span>
              <span className="brand-subtitle">کلینیک روان‌شناسی اوج</span>
            </div>
          </NavLink>

          {/* Desktop Navigation */}
          <nav className="header-nav">
            <NavLink to="/" className="nav-item">
              صفحه اصلی
            </NavLink>

            <div className="nav-item" onClick={() => handleNavClick("about")}>
              درباره دکتر
            </div>

            <div className="nav-item" onClick={() => handleNavClick("services")}>
              خدمات
            </div>

            <div className="nav-dropdown-container">
              <NavLink to="/store" className="nav-item with-icon">
                فروشگاه <BiChevronDown />
              </NavLink>
              <div className="dropdown-menu mega-menu">
                <div className="mega-menu-content">
                  {STATIC_CATEGORIES.map((item) => (
                    <div key={item.id} className="mega-menu-column">
                      <NavLink
                        to={`/category/${item.id}`}
                        className="mega-menu-title"
                      >
                        {item.txt}
                        <BiChevronLeft />
                      </NavLink>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <NavLink to="/reservation-form" className="nav-item highlight">
              رزرو نوبت
            </NavLink>

            <div className="nav-item" onClick={() => handleNavClick("contact")}>
              تماس با ما
            </div>
          </nav>

          {/* Actions */}
          <div className="header-actions">
            <div className="search-wrapper" ref={searchRef}>
              <button
                className={`action-btn ${showSearch ? 'active' : ''}`}
                aria-label="Search"
                onClick={() => setShowSearch(!showSearch)}
              >
                {showSearch ? <BiX /> : <BiSearch />}
              </button>

              {showSearch && (
                <div className="search-dropdown">
                  <div className="search-input-wrapper">
                    <BiSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="جستجو در محصولات و مقالات..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      autoFocus
                    />
                  </div>

                  {(products.length > 0 || foundCategories.length > 0 || articles.length > 0) && (
                    <div className="search-results">
                      {products.length > 0 && (
                        <div className="search-section">
                          <h6>محصولات</h6>
                          {products.map(p => (
                            <NavLink key={p.id} to={`/product/${p.id}`} className="search-result-item" onClick={() => setShowSearch(false)}>
                              {p.title}
                            </NavLink>
                          ))}
                        </div>
                      )}

                      {foundCategories.length > 0 && (
                        <div className="search-section">
                          <h6>دسته‌بندی‌ها</h6>
                          {foundCategories.map(c => (
                            <NavLink key={c.id} to={`/category/${c.id}`} className="search-result-item" onClick={() => setShowSearch(false)}>
                              {c.title || c.name}
                            </NavLink>
                          ))}
                        </div>
                      )}

                      {articles.length > 0 && (
                        <div className="search-section">
                          <h6>مقالات</h6>
                          {articles.map(a => (
                            <NavLink key={a.id} to={`/blog/post/${a.id}`} className="search-result-item" onClick={() => setShowSearch(false)}>
                              {a.title}
                            </NavLink>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {searchQuery && products.length === 0 && foundCategories.length === 0 && articles.length === 0 && (
                    <div className="no-results">
                      نتیجه‌ای یافت نشد
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              className="action-btn"
              onClick={() => setShowOffcanvas(true)}
              aria-label="Cart"
            >
              <BiCart />
              {itemCount > 0 && <span className="cart-badge">{itemCount}</span>}
            </button>

            {auth.isLogged ? (
              <Dropdown align="end" className="user-dropdown">
                <Dropdown.Toggle as="button" className="action-btn" id="user-dropdown">
                  <BiUser />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {auth.userInfo.userType == 0 ? (
                    <>
                      <Dropdown.Item onClick={() => setControllerProduct({ show: true, type: "add" })}>
                        <BiPlus /> افزودن محصول
                      </Dropdown.Item>
                      <Dropdown.Item onClick={() => { setShowModalArticle(prev => !prev); setType("add"); }}>
                        <BiNews /> افزودن مقاله
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item href="/admin-panel/orders">
                        <BiPackage /> سفارشات
                      </Dropdown.Item>
                      <Dropdown.Item href="/admin-panel/user-management">
                        <BiUserCircle /> مدیریت کاربران
                      </Dropdown.Item>
                      <Dropdown.Item href="/admin-panel/category-management">
                        <BiCategory /> مدیریت دسته بندی
                      </Dropdown.Item>
                      <Dropdown.Item href="/admin-panel/option-management">
                        <BiCog /> مدیریت ویژگی ها
                      </Dropdown.Item>
                      <Dropdown.Item href="/admin-panel/brand-management">
                        <BiCog /> مدیریت برندها
                      </Dropdown.Item>
                      <Dropdown.Item href="/admin-panel/appointment-management">
                        <BiCog /> مدیریت رزروها
                      </Dropdown.Item>
                    </>
                  ) : (
                    <>
                      <Dropdown.Item href="/customer-panel/profile">
                        <BiUserCircle /> پروفایل
                      </Dropdown.Item>
                      <Dropdown.Item href="/customer-panel/orders">
                        <BiPackage /> سفارشات
                      </Dropdown.Item>
                      <Dropdown.Item href="/customer-panel/favorites">
                        <BiHeart /> علاقه‌مندی‌ها
                      </Dropdown.Item>
                    </>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => { auth.logout(); navigate('/'); }}>
                    <BiLogOut /> خروج
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            ) : (
              <button
                className="action-btn"
                onClick={() => setLoginShow(true)}
                aria-label="User Profile"
              >
                <BiUser />
              </button>
            )}

            <button className="mobile-toggle" onClick={toggleMobileMenu}>
              {mobileMenuOpen ? <BiX /> : <FaBars />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>
          <div className="mobile-menu-content">
            <NavLink to="/" className="mobile-nav-item" onClick={() => setMobileMenuOpen(false)}>
              صفحه اصلی
            </NavLink>
            <div className="mobile-nav-item" onClick={() => handleNavClick("about")}>
              درباره دکتر
            </div>
            <div className="mobile-nav-item" onClick={() => handleNavClick("services")}>
              خدمات
            </div>
            <NavLink to="/reservation-form" className="mobile-nav-item highlight" onClick={() => setMobileMenuOpen(false)}>
              رزرو نوبت
            </NavLink>
            <NavLink to="/store" className="mobile-nav-item" onClick={() => setMobileMenuOpen(false)}>
              فروشگاه
            </NavLink>
            <div className="mobile-divider">دسته‌بندی‌ها</div>
            <div className="mobile-categories-grid">
              {STATIC_CATEGORIES.map((item) => (
                <NavLink
                  key={item.id}
                  to={`/category/${item.id}`}
                  className="mobile-category-card"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <div className="category-icon-wrapper">
                    {item.icon}
                  </div>
                  <span className="category-text">{item.txt}</span>
                </NavLink>
              ))}
            </div>
            <div className="mobile-nav-item" onClick={() => handleNavClick("contact")}>
              تماس با ما
            </div>
            <div className="mobile-contact-info">
              <span>شماره تماس:</span>
              <a href="tel:09304521003">09304521003</a>
            </div>
          </div>
        </div>
      </header>

      {/* Login Modal */}
      <Container
        loginShow={loginShow}
        setLoginShow={setLoginShow}
      />

      {/* Cart Basket */}
      <Basket />
    </>
  );
};

export default Header;
